// src/components/empleado/EmpleadoPanel.jsx
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

// Servicios / UI
import { api } from '../../services/api';
import Header from '../shared/Header';
import Paginacion from '../shared/Paginacion';
import FichajeBloque from './FichajeBloque';
import SolicitudManualForm from './SolicitudManualForm.jsx';

// Utils de fecha
import { parseISO, yyyymmddTZ, fmtHoraISO, fmtFechaISO } from '../../utils/fecha';
import { LABELS, formatDuracionMin, calcularResumenDia, detectarDescansoConstante } from '../../utils/tiempos';
import KpiRow from '../shared/KpiRow';

// ✅ Geo (centralizado)
import { getBrowserPosition } from '../../utils/geo';

/* ============================
   Helpers varios
   ============================ */
const formateaAviso = (iso) => {
  if (!iso) return '';
  const ts = parseISO(iso);
  if (!ts || isNaN(ts)) return '';
  const hoyISO = yyyymmddTZ(new Date());
  const diaISO = yyyymmddTZ(ts);
  const diffDias = Math.floor(
    (new Date(hoyISO + 'T00:00:00Z') - new Date(diaISO + 'T00:00:00Z')) / 86400000
  );
  const hora = fmtHoraISO(ts.toISOString());
  if (diffDias === 0) return `desde las ${hora}`;
  if (diffDias === 1) return `desde Ayer a las ${hora}`;
  return `desde el ${fmtFechaISO(ts.toISOString())} a las ${hora} (hace ${diffDias} días)`;
};

function classifyQuality(acc) {
  if (typeof acc !== 'number' || !isFinite(acc)) return 'low';
  if (acc <= 50) return 'high';
  if (acc <= 200) return 'medium';
  return 'low';
}

// Wrapper para mantener la firma getPositionOnce y centralizar la geo en utils/geo.js
async function getPositionOnce(opts = {}) {
  return getBrowserPosition(opts); // devuelve { lat, lng, accuracy }
}

async function geolocationPermissionGranted() {
  try {
    if (!('permissions' in navigator) || !navigator.permissions.query) return false;
    const st = await navigator.permissions.query({ name: 'geolocation' });
    return st.state === 'granted';
  } catch {
    return false;
  }
}

const getISO = (v) => {
  if (!v) return null;
  if (typeof v === 'string') return v;
  if (typeof v === 'object') return v.timestamp || v.ts || v.iso || null;
  if (typeof v === 'number') return new Date(v).toISOString();
  return null;
};

const pickLabel = (obj) => {
  if (!obj || typeof obj !== 'object') return null;
  return (
    obj.loc_label ||
    obj.locLabel ||
    obj.label ||
    (obj.loc && (obj.loc.label || obj.loc.name)) ||
    null
  );
};

const tsKey = (tipo, iso) => {
  const d = parseISO(iso);
  if (!d || isNaN(d)) return null;
  return `${(tipo || '').toLowerCase()}|${d.getTime()}`;
};

const hasMissingLabels = (resumen) => {
  for (const [k, day] of Object.entries(resumen || {})) {
    if (k === '_meta' || !day || !Array.isArray(day.bloques)) continue;
    for (const b of day.bloques) {
      if (b?.ausencia) continue;
      const eISO = getISO(b?.entrada);
      const sISO = getISO(b?.salida);
      const eLbl = pickLabel(b?.entrada);
      const sLbl = pickLabel(b?.salida);
      if ((!!eISO && !eLbl) || (!!sISO && !sLbl)) return true;
    }
  }
  return false;
};

/* ============================
   Componente
   ============================ */
export default function EmpleadoPanel({ session, onLogout }) {
  const [resumenFichajes, setResumenFichajes] = useState({});
  const [meta, setMeta] = useState({});
  const [tiempoHoy, setTiempoHoy] = useState(0);
  const [mostrarResumen, setMostrarResumen] = useState(false);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [estadoActual, setEstadoActual] = useState(null);
  const [loadingFichar, setLoadingFichar] = useState(false);
  const [cargandoResumen, setCargandoResumen] = useState(false);
  const [pagina, setPagina] = useState(1);
  const porPagina = 3;

  const { token, user } = session || {};
  const role = (session?.role || '').toLowerCase();
  const isAdmin = role === 'admin';

  const usuarioEmail = (typeof user === 'object' && user?.email) ? user.email : user;

  // ---------- Rescate branding para Header (igual que en AdminPanel) ----------
  const userObj = (typeof user === 'object' && user) ? user : {};
  const [me, setMe] = useState(null);

  useEffect(() => {
    let cancel = false;
    async function loadMe() {
      if (userObj?.brand_dir && userObj?.company_name) return;
      if (!token) return;
      try {
        const data = await api.get('/auth/me', token);
        if (!cancel && data?.user) setMe(data.user);
      } catch {
        try {
          if (cancel) return;
          const data2 = await api.get('/me', token);
          if (!cancel && data2?.user) setMe(data2.user);
        } catch { /* noop */ }
      }
    }
    loadMe();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userObj?.brand_dir, userObj?.company_name]);

  const brandForHeader = userObj?.brand_dir || me?.brand_dir || null;
  const companyForHeader = userObj?.company_name || me?.company_name || null;
  const pausaFijaMin = Number(userObj?.pausa_comida_minutos ?? me?.pausa_comida_minutos ?? 0) || 0;
  const pausaFijaEnabled = !!(userObj?.descuenta_pausa_en_resumen ?? me?.descuenta_pausa_en_resumen ?? false);

  const hoyISO = yyyymmddTZ(new Date());
  const hoyDate = new Date(`${hoyISO}T00:00:00Z`);

  const sessGeoEnabled = !!userObj?.geo_enabled;
  const sessGeoRequired = !!userObj?.geo_required;

  const fetchGeoFlags = useCallback(async () => {
    try {
      const meResp = await api.get('/auth/me', token); // { user: {...} }
      const u = meResp?.user || {};
      return {
        geo_enabled: !!u.geo_enabled,
        geo_required: !!u.geo_required,
        geo_quality_min: u.geo_quality_min || null,
      };
    } catch {
      return {
        geo_enabled: sessGeoEnabled,
        geo_required: sessGeoRequired,
        geo_quality_min: userObj?.geo_quality_min || null,
      };
    }
  }, [token, sessGeoEnabled, sessGeoRequired, userObj?.geo_quality_min]);

  const enrichResumenWithLocLabels = useCallback(
    async (base) => {
      const flags = await fetchGeoFlags();
      if (!flags.geo_enabled) return base;
      if (!hasMissingLabels(base)) return base;

      let fichajes = [];
      try {
        fichajes = await api.get('/fichajes/me', token);
      } catch {
        return base;
      }

      const labelMap = {};
      for (const f of fichajes || []) {
        if (!f?.timestamp || !f?.tipo) continue;
        const key = tsKey(f.tipo, f.timestamp);
        const lbl = pickLabel(f);
        if (key && lbl) labelMap[key] = lbl;
      }
      if (Object.keys(labelMap).length === 0) return base;

      const updated = JSON.parse(JSON.stringify(base));
      for (const [k, day] of Object.entries(updated || {})) {
        if (k === '_meta' || !day || !Array.isArray(day.bloques)) continue;
        for (const b of day.bloques) {
          if (b?.ausencia) continue;

          const eISO = getISO(b?.entrada);
          if (eISO && !pickLabel(b?.entrada)) {
            const k1 = tsKey('entrada', eISO);
            const lbl = k1 ? labelMap[k1] : null;
            if (lbl) {
              if (!b.entrada || typeof b.entrada !== 'object') b.entrada = { timestamp: eISO };
              b.entrada.loc_label = lbl;
            }
          }

          const sISO = getISO(b?.salida);
          if (sISO && !pickLabel(b?.salida)) {
            const k2 = tsKey('salida', sISO);
            const lbl = k2 ? labelMap[k2] : null;
            if (lbl) {
              if (!b.salida || typeof b.salida !== 'object') b.salida = { timestamp: sISO };
              b.salida.loc_label = lbl;
            }
          }
        }
      }
      return updated;
    },
    [token, fetchGeoFlags]
  );

  const cargarResumenFichajes = useCallback(async () => {
    setCargandoResumen(true);
    try {
      const data = await api.get('/resumen-fichajes', token);
      const base = data?.resumen || data || {};
      const metaRx = data?._meta || {};

      const enriquecido = await enrichResumenWithLocLabels(base);

      setResumenFichajes(enriquecido);
      setMeta(metaRx);

      if (metaRx?.turno_abierto?.desde) {
        toast.warning(`Tienes un turno abierto ${formateaAviso(metaRx.turno_abierto.desde)}.`, { autoClose: 7000 });
      }
      if (Array.isArray(metaRx?.fichajes_futuros) && metaRx.fichajes_futuros.length > 0) {
        toast.error('⚠️ Se han detectado fichajes con fecha FUTURA. Corrige antes de continuar.', { autoClose: 10000 });
      }
    } catch (e) {
      toast.error(`❌ Error al cargar el resumen de fichajes${e?.message ? `: ${e.message}` : ''}`);
      setResumenFichajes({});
      setMeta({});
    } finally {
      setCargandoResumen(false);
    }
  }, [token, enrichResumenWithLocLabels]);

  const tieneFuturos = Array.isArray(meta?.fichajes_futuros) && meta.fichajes_futuros.length > 0;

  // Construye FormData y añade geo si corresponde
  const buildFormWithMaybeGeo = async (tipo) => {
    const form = new FormData();
    form.append('tipo', tipo);

    const flags = await fetchGeoFlags();
    const needGeo = !!flags.geo_enabled && !!flags.geo_required;
    const geoEnabled = !!flags.geo_enabled;

    if (needGeo) {
      const pos = await getPositionOnce();
      const { lat, lng, accuracy } = pos || {};
      const q = classifyQuality(accuracy);

      form.append('lat', String(lat));
      form.append('lng', String(lng));
      if (typeof accuracy === 'number' && isFinite(accuracy)) form.append('accuracy', String(accuracy));
      form.append('quality', q);
      return form;
    }

    if (geoEnabled && (await geolocationPermissionGranted())) {
      try {
        const pos = await getPositionOnce({ timeout: 5000 });
        const { lat, lng, accuracy } = pos || {};
        const q = classifyQuality(accuracy);

        form.append('lat', String(lat));
        form.append('lng', String(lng));
        if (typeof accuracy === 'number' && isFinite(accuracy)) form.append('accuracy', String(accuracy));
        form.append('quality', q);
      } catch {
        /* sin geo opcional */
      }
    }

    return form;
  };

  const fichar = async (tipo) => {
    if (tieneFuturos) {
      toast.error('⛔ Fichaje bloqueado por fichajes futuros. Corrige primero.');
      return;
    }

    if (tipo === 'entrada' && turnoAbiertoISO) {
      toast.info('Tienes un turno abierto. Si ya creaste una solicitud de salida, se usará automáticamente; si no, el servidor rechazará la nueva entrada.');
    }

    setLoadingFichar(true);
    let triedRetry = false;

    try {
      let form = await buildFormWithMaybeGeo(tipo);

      const doPost = async () => {
        if (typeof api.postForm === 'function') {
          await api.postForm('/fichar', form, token);
        } else {
          await api.post('/fichar', form, token);
        }
      };

      try {
        await doPost();
      } catch (err1) {
        const msg = (err1?.message || '').toLowerCase();
        if (!triedRetry && /ubicaci[oó]n.*requerid/.test(msg)) {
          triedRetry = true;
          try {
            form = new FormData();
            form.append('tipo', tipo);

            const pos = await getPositionOnce();
            const { lat, lng, accuracy } = pos || {};
            const q = classifyQuality(accuracy);

            form.append('lat', String(lat));
            form.append('lng', String(lng));
            if (typeof accuracy === 'number' && isFinite(accuracy)) form.append('accuracy', String(accuracy));
            form.append('quality', q);

            await doPost();
          } catch (err2) {
            throw err2;
          }
        } else {
          throw err1;
        }
      }

      toast.success(tipo === 'entrada' ? '✅ Entrada registrada' : '✅ Salida registrada');
      await cargarResumenFichajes();
    } catch (e) {
      const code = e?.code;
      if (code === 1) {
        toast.error('Se requiere autorización de ubicación para fichar en tu empresa.');
      } else if (code === 2) {
        toast.error('No se pudo determinar tu ubicación (posicionamiento no disponible).');
      } else if (code === 3) {
        toast.error('Tiempo de espera agotado al obtener la ubicación.');
      } else {
        toast.error(`❌ ${e?.message || 'Error al fichar'}`);
      }
    } finally {
      setLoadingFichar(false);
    }
  };

  useEffect(() => { cargarResumenFichajes(); }, [cargarResumenFichajes]);

  const bloquesHoy = useMemo(() => {
    return (resumenFichajes[hoyISO]?.bloques || []).filter(b => !b?.ausencia);
  }, [resumenFichajes, hoyISO]);

  const bloquesCerradosHoy = useMemo(() => {
    return bloquesHoy.filter(b => b?.salida || b?.cerrado === true);
  }, [bloquesHoy]);

  const turnoAbiertoISO = useMemo(() => {
    if (meta?.turno_abierto?.desde) return meta.turno_abierto.desde;
    const abierto = bloquesHoy.find(b => b?.entrada && !b?.salida);
    return getISO(abierto?.entrada);
  }, [meta?.turno_abierto?.desde, bloquesHoy]);

  const totalCerradoHoy = useMemo(
    () => bloquesCerradosHoy.reduce((acc, b) => acc + (Number.isFinite(b?.duracion) ? b.duracion : 0), 0),
    [bloquesCerradosHoy]
  );

  useEffect(() => {
    const tick = () => {
      let extra = 0;
      const abiertoIso = turnoAbiertoISO;
      if (abiertoIso) {
        const inicio = parseISO(abiertoIso);
        if (inicio && !isNaN(inicio)) {
          const baseTs = Math.max(
            inicio.getTime(),
            new Date(`${hoyISO}T00:00:00Z`).getTime()
          );
          extra = Math.max(0, Math.floor((Date.now() - baseTs) / 1000));
        }
      }
      setTiempoHoy(totalCerradoHoy + extra);
      setEstadoActual(abiertoIso ? 'fichado' : 'descanso');
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [turnoAbiertoISO, hoyISO, totalCerradoHoy]);

  const formatMin = (mins = 0) => formatDuracionMin(mins);
  const formatSeg = (secs = 0) => {
    const total = Math.max(0, Math.floor(Number(secs) || 0));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  /* ============================
     Render
     ============================ */
  return (
    <div
      className="min-h-screen relative"
      style={{
        backgroundImage: "url('/fondo.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/10 to-transparent" />

      <Header
        usuario={user}                         // objeto o email; Header lo normaliza
        brandDir={brandForHeader}              // ✅ forzado (user.brand_dir o /auth/me)
        companyName={companyForHeader}         // ✅ forzado (user.company_name o /auth/me)
        onLogout={onLogout}
        showSwitchVista={isAdmin}
        switchLabel="Vista admin"
        switchToPath="/admin"
      />

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Tarjeta de fichaje */}
        <section className="rounded-2xl bg-white/60 backdrop-blur-md border border-white/40 shadow p-4 sm:p-5">
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            <h3 className="text-lg font-semibold text-[#004B87]">Fichaje</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={cargarResumenFichajes}
                className="text-xs sm:text-sm px-3 py-1 rounded-lg border hover:bg-slate-50"
                disabled={cargandoResumen}
                title="Recargar resumen"
              >
                {cargandoResumen ? 'Actualizando…' : 'Refrescar'}
              </button>
            </div>
          </div>

          {(!!userObj?.geo_enabled || !!userObj?.geo_required) && (
            <div className="mb-3 text-xs text-slate-600">
              {userObj?.geo_required
                ? '📍 Tu empresa requiere ubicación para fichar. Se solicitará al pulsar.'
                : '📍 Tu empresa permite adjuntar ubicación (opcional).'}
            </div>
          )}

          {turnoAbiertoISO && (
            <div className="mb-4 rounded-xl border border-amber-300/60 bg-amber-50/80 p-3 sm:p-4 text-amber-900">
              <div className="font-semibold mb-1 sm:mb-2">
                🕒 Tienes un turno abierto {formateaAviso(turnoAbiertoISO)}
              </div>
              <div className="text-xs sm:text-sm text-gray-700">
                Puedes fichar <b>salida ahora</b> o enviar una <b>solicitud manual</b> con la hora real.
              </div>
            </div>
          )}

          <div className="flex gap-3 sm:gap-4 flex-col sm:flex-row">
            <button
              disabled={loadingFichar || tieneFuturos}
              onClick={() => fichar('entrada')}
              className={`w-full sm:w-auto justify-center flex items-center gap-2 px-5 sm:px-6 py-3 rounded-2xl text-base font-semibold text-white shadow-md transition-all duration-200 ${
                (loadingFichar || tieneFuturos)
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-green-500 to-green-600 hover:scale-[1.02]'
              }`}
              title={tieneFuturos ? 'Bloqueado por fichaje futuro' : 'Fichar entrada'}
            >
              🟢 Fichar entrada
            </button>

            <button
              disabled={loadingFichar || !turnoAbiertoISO || tieneFuturos}
              onClick={() => fichar('salida')}
              className={`w-full sm:w-auto justify-center flex items-center gap-2 px-5 sm:px-6 py-3 rounded-2xl text-base font-semibold text-white shadow-md transition-all duration-200 ${
                (!turnoAbiertoISO || tieneFuturos)
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:scale-[1.02]'
              }`}
              title={
                tieneFuturos
                  ? 'Bloqueado por fichaje futuro'
                  : (!turnoAbiertoISO ? 'No hay turno abierto' : 'Fichar salida')
              }
            >
              🔵 Fichar salida
            </button>
          </div>

          {tieneFuturos && (
            <p className="mt-3 text-xs sm:text-sm text-red-700">
              ⛔ Fichaje bloqueado por fichajes con fecha FUTURA. Corrige esos registros o solicita al admin que los ajuste.
            </p>
          )}

          {estadoActual && (
            <div className="mt-2 text-sm flex items-center gap-2">
              {estadoActual === 'fichado'
                ? <span className="text-green-700">🟢 Fichado (trabajando)</span>
                : <span className="text-red-600">🔴 En descanso</span>}
            </div>
          )}

          <div className="mt-4 text-sm text-gray-800 space-y-2">
            <p>
              ⏰ <strong>Tiempo efectivo hoy:</strong>{" "}
              {turnoAbiertoISO ? formatSeg(tiempoHoy) : formatMin(Math.floor(tiempoHoy / 60))}
            </p>
            {(resumenFichajes[hoyISO]?.bloques || [])
              .filter(b => !b?.ausencia)
              .map((b, i) => (
                <FichajeBloque key={i} entrada={b.entrada} salida={b.salida} duracion={b.duracion} anomalia={b.anomalia} />
              ))}
          </div>
        </section>

        {/* Atajos Ausencias & Vacaciones */}
        <section className="rounded-2xl bg-white/60 backdrop-blur-md border border-white/40 shadow p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-[#004B87]">Ausencias y Vacaciones</h3>
            <div className="flex gap-2">
              <Link
                to="/ausencias?tab=solicitar"
                className="px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-sm"
              >
                ➕ Solicitar
              </Link>
              <Link
                to="/ausencias?tab=mis"
                className="px-3 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-800 text-sm"
              >
                📄 Mis solicitudes
              </Link>
              <Link
                to="/ausencias?tab=saldos"
                className="px-3 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-800 text-sm"
                title="Ver mis saldos de vacaciones/LD"
              >
                💼 Saldos
              </Link>
            </div>
          </div>
        </section>

        {/* Solicitud de fichaje manual */}
        <section className="rounded-2xl bg-white/60 backdrop-blur-md border border-white/40 shadow p-4 sm:p-5">
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            <h3 className="text-lg font-semibold text-[#004B87]">Solicitar fichaje manual</h3>
            <button onClick={() => setMostrarFormulario(!mostrarFormulario)} className="text-sm text-indigo-600 hover:underline">
              {mostrarFormulario ? 'Ocultar' : 'Mostrar más'}
            </button>
          </div>
          {mostrarFormulario && (
            <SolicitudManualForm usuarioEmail={usuarioEmail} token={token} />
          )}
        </section>

        {/* Resumen de fichajes por día */}
        <section className="rounded-2xl bg-white/60 backdrop-blur-md border border-white/40 shadow p-4 sm:p-5">
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            <h3 className="text-lg font-semibold text-[#004B87]">Tus fichajes</h3>
            <button onClick={() => setMostrarResumen(!mostrarResumen)} className="text-sm text-indigo-600 hover:underline">
              {mostrarResumen ? 'Ocultar' : 'Ver resumen'} por día
            </button>
          </div>

          {mostrarResumen && (() => {
            const diasConBloques = Object.entries(resumenFichajes || {})
              .filter(([k, d]) => {
                if (k === '_meta') return false;
                if (!Array.isArray(d?.bloques)) return false;
                if (new Date(`${k}T00:00:00Z`) > hoyDate) return false;
                return d.bloques.some(
                  (b) => b && !b.ausencia && (b.entrada || b.salida || Number.isFinite(b.duracion))
                );
              })
              .sort(([a], [b]) => new Date(b) - new Date(a));

            const diasProcesados = diasConBloques.map(([dia, info]) => {
              const bloques = (info?.bloques || []).filter((b) => !b?.ausencia);
              const intervalos = bloques
                .filter((b) => b?.entrada)
                .map((b) => ({
                  inicio: b?.entrada,
                  fin: b?.salida,
                  duracion: typeof b?.duracion === "number" ? b.duracion : null,
                }));

              const resumenDia = calcularResumenDia(intervalos, {
                pausaFijaMin,
                pausaFijaEnabled,
              });

              return { dia, bloques, resumenDia };
            });

            const totalPaginas = Math.max(1, Math.ceil(diasProcesados.length / porPagina));
            const inicio = (pagina - 1) * porPagina;
            const visibles = diasProcesados.slice(inicio, inicio + porPagina);

            const resumenGlobal = (() => {
              const dias = diasProcesados.map((d) => ({
                descansoMin: Number(d.resumenDia?.descansoMin || 0),
                efectivoMin: Number(d.resumenDia?.efectivoMin || 0),
                jornadaMin: Number(d.resumenDia?.jornadaMin || 0),
              }));
              const totalDias = dias.length;
              const totalEfectivo = dias.reduce((acc, d) => acc + (d.efectivoMin || 0), 0);
              const descInfo = detectarDescansoConstante(dias, { toleranceMin: 5 });
              return { totalDias, totalEfectivo, descInfo };
            })();

            return (
              <>
                {diasProcesados.length > 0 && (() => {
                  const descInfo = resumenGlobal.descInfo || {};
                  const descTotalTxt = formatMin(descInfo.totalDescansoMin || 0);
                  const descConstante = !!descInfo.esConstante;
                  const descLinea = descConstante
                    ? `${descInfo.diasConDescanso} x ${formatMin(descInfo.valorBaseMin || 0)} = ${descTotalTxt}`
                    : descTotalTxt;

                  let descExtra = "";
                  if (!descConstante && (descInfo.diasConDescanso || 0) >= 2) {
                    const mediaTxt = formatMin(descInfo.mediaDescansoMin || 0);
                    const minTxt = formatMin(descInfo.minDescansoMin || 0);
                    const maxTxt = formatMin(descInfo.maxDescansoMin || 0);
                    descExtra = `Media descanso/día: ${mediaTxt}`;
                    if (minTxt !== maxTxt) descExtra += ` - Min-Max: ${minTxt}-${maxTxt}`;
                  }

                  const mediaEfectiva =
                    resumenGlobal.totalDias >= 2
                      ? formatMin(Math.round(resumenGlobal.totalEfectivo / resumenGlobal.totalDias))
                      : "";

                  return (
                    <div className="rounded-xl border border-slate-200 bg-white/70 p-3">
                      <div className="text-[11px] uppercase tracking-wide text-slate-500">Resumen global</div>
                      <div className="mt-2 flex flex-wrap gap-6">
                        <div>
                          <div className="text-xs text-slate-500">{LABELS.EFECTIVO} total</div>
                          <div className="text-base font-semibold text-emerald-700">
                            {formatMin(resumenGlobal.totalEfectivo)}
                          </div>
                          {mediaEfectiva && (
                            <div className="text-xs text-slate-500">Media efectiva/día: {mediaEfectiva}</div>
                          )}
                        </div>
                        <div>
                          <div className="text-xs text-slate-500">{LABELS.DESCANSO} total</div>
                          <div className="text-sm font-semibold text-slate-700">{descLinea}</div>
                          {descExtra && <div className="text-xs text-slate-500">{descExtra}</div>}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div className="space-y-3">
                  {visibles.map(({ dia, bloques, resumenDia }) => {
                    return (
                      <div key={dia} className="rounded-xl border border-white/60 bg-white/70 p-3">
                        <div className="text-sm font-semibold text-slate-800 mb-1">
                          {new Date(`${dia}T00:00:00Z`).toLocaleDateString('es-ES')}
                        </div>
                        <div className="space-y-1">
                          {bloques.map((b, i) => (
                            <FichajeBloque
                              key={`${dia}-${i}`}
                              entrada={b.entrada}
                              salida={b.salida}
                              duracion={b.duracion}
                              anomalia={b.anomalia}
                            />
                          ))}
                        </div>

                        <div className="mt-2 text-xs sm:text-sm text-slate-700">
                          {resumenDia.diaAbierto ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-50 text-slate-700 border border-slate-200 text-[11px] sm:text-xs">
                              <span className="font-medium">{LABELS.JORNADA} (en curso):</span>
                              <strong>{formatMin(resumenDia.jornadaMin)}</strong>
                            </span>
                          ) : (
                            <KpiRow
                              jornada={formatMin(resumenDia.jornadaMin)}
                              descanso={formatMin(resumenDia.descansoMin)}
                              efectivo={formatMin(resumenDia.efectivoMin)}
                            />
                          )}
                          {resumenDia.diaAbierto && (
                            <div className="text-amber-700 mt-1">
                              Dia abierto - Descanso no aplicado
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {totalPaginas > 1 && (
                  <div className="mt-3">
                    <Paginacion
                      pagina={pagina}
                      totalPaginas={totalPaginas}
                      onAnterior={() => setPagina((p) => Math.max(1, p - 1))}
                      onSiguiente={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                      onGoToPage={(n) => setPagina(n)}
                    />
                  </div>
                )}
              </>
            );
          })()}
        </section>
      </main>
    </div>
  );
}
